import { Problem, ProblemType, AdvancedOptions } from '../types'

const generateRandomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

// Helper to generate a simple fraction
const generateFraction = () => {
  const denominators = [2, 3, 4, 5, 6, 8, 10, 12]
  const denominator =
    denominators[Math.floor(Math.random() * denominators.length)]
  const numerator = generateRandomInt(1, denominator - 1)
  return { numerator, denominator }
}

// Helper to simplify a fraction
const gcd = (a: number, b: number): number => {
  return b === 0 ? a : gcd(b, a % b)
}

const simplifyFraction = (num: number, den: number) => {
  const divisor = gcd(Math.abs(num), Math.abs(den))
  return { numerator: num / divisor, denominator: den / divisor }
}

// Helper to format fraction as string
const formatFraction = (num: number, den: number) => {
  if (den === 1) return num.toString()
  return `${num}/${den}`
}

// Helper to add fractions
const addFractions = (n1: number, d1: number, n2: number, d2: number) => {
  const numerator = n1 * d2 + n2 * d1
  const denominator = d1 * d2
  return simplifyFraction(numerator, denominator)
}

// Helper to subtract fractions
const subtractFractions = (n1: number, d1: number, n2: number, d2: number) => {
  const numerator = n1 * d2 - n2 * d1
  const denominator = d1 * d2
  return simplifyFraction(numerator, denominator)
}

// Helper to multiply fractions
const multiplyFractions = (n1: number, d1: number, n2: number, d2: number) => {
  const numerator = n1 * n2
  const denominator = d1 * d2
  return simplifyFraction(numerator, denominator)
}

// Helper to divide fractions
const divideFractions = (n1: number, d1: number, n2: number, d2: number) => {
  const numerator = n1 * d2
  const denominator = d1 * n2
  return simplifyFraction(numerator, denominator)
}

const applyNumberType = (
  num: number,
  numberType: 'positive' | 'negative' | 'both',
): number => {
  if (numberType === 'positive') return Math.abs(num)
  if (numberType === 'negative') return -Math.abs(num)
  // 'both' - randomly make negative
  return Math.random() > 0.5 ? num : -num
}

const getNumberRange = (
  difficulty: 'easy' | 'medium' | 'hard',
  digitCount: number,
) => {
  const baseRanges = {
    easy: { min: 1, max: 20 },
    medium: { min: 10, max: 100 },
    hard: { min: 50, max: 500 },
  }

  const range = baseRanges[difficulty]

  // Adjust based on digit count
  if (digitCount === 1) {
    return { min: 1, max: 9 }
  } else if (digitCount === 2) {
    return range
  } else if (digitCount === 3) {
    return { min: 100, max: 999 }
  } else {
    return {
      min: Math.pow(10, digitCount - 1),
      max: Math.pow(10, digitCount) - 1,
    }
  }
}

export const generateProblem = (
  allowedTypes: ProblemType[],
  options: AdvancedOptions,
): Problem => {
  const types =
    allowedTypes.length > 0
      ? allowedTypes
      : ['addition', 'subtraction', 'multiplication', 'division']
  const selectedType = types[Math.floor(Math.random() * types.length)]

  const range = getNumberRange(options.difficulty, options.digitCount)

  // Determine if we should use fractions for this problem
  const isBasicOperation = [
    'addition',
    'subtraction',
    'multiplication',
    'division',
  ].includes(selectedType)
  const useFractions =
    isBasicOperation &&
    options.orientation === 'horizontal' &&
    (options.valueType === 'fractions' ||
      (options.valueType === 'both' && Math.random() > 0.5))

  let a = 0
  let b = 0
  let answer: number | string = 0
  let question = ''

  if (useFractions && isBasicOperation) {
    // Generate fraction problems
    const frac1 = generateFraction()
    const frac2 = generateFraction()

    let result: { numerator: number; denominator: number }

    switch (selectedType) {
      case 'addition':
        result = addFractions(
          frac1.numerator,
          frac1.denominator,
          frac2.numerator,
          frac2.denominator,
        )
        question = `${formatFraction(frac1.numerator, frac1.denominator)} + ${formatFraction(frac2.numerator, frac2.denominator)}`
        answer = formatFraction(result.numerator, result.denominator)
        break

      case 'subtraction':
        result = subtractFractions(
          frac1.numerator,
          frac1.denominator,
          frac2.numerator,
          frac2.denominator,
        )
        question = `${formatFraction(frac1.numerator, frac1.denominator)} - ${formatFraction(frac2.numerator, frac2.denominator)}`
        answer = formatFraction(result.numerator, result.denominator)
        break

      case 'multiplication':
        result = multiplyFractions(
          frac1.numerator,
          frac1.denominator,
          frac2.numerator,
          frac2.denominator,
        )
        question = `${formatFraction(frac1.numerator, frac1.denominator)} × ${formatFraction(frac2.numerator, frac2.denominator)}`
        answer = formatFraction(result.numerator, result.denominator)
        break

      case 'division':
        result = divideFractions(
          frac1.numerator,
          frac1.denominator,
          frac2.numerator,
          frac2.denominator,
        )
        question = `${formatFraction(frac1.numerator, frac1.denominator)} ÷ ${formatFraction(frac2.numerator, frac2.denominator)}`
        answer = formatFraction(result.numerator, result.denominator)
        break
    }
  } else {
    // Generate integer problems (existing logic)
    switch (selectedType) {
      case 'addition':
        a = generateRandomInt(range.min, range.max)
        b = generateRandomInt(range.min, range.max)
        a = applyNumberType(a, options.numberType)
        b = applyNumberType(b, options.numberType)
        answer = a + b
        question =
          options.orientation === 'horizontal' ? `${a} + ${b}` : `${a}\n+\n${b}`
        break

      case 'subtraction':
        a = generateRandomInt(range.min, range.max)
        b = generateRandomInt(range.min, Math.abs(a))
        a = applyNumberType(a, options.numberType)
        b = applyNumberType(b, options.numberType)
        answer = a - b
        question =
          options.orientation === 'horizontal' ? `${a} - ${b}` : `${a}\n-\n${b}`
        break

      case 'multiplication':
        const multRange =
          options.difficulty === 'easy' ? { min: 2, max: 12 } : range
        a = generateRandomInt(multRange.min, multRange.max)
        b = generateRandomInt(multRange.min, multRange.max)
        a = applyNumberType(a, options.numberType)
        b = applyNumberType(b, options.numberType)
        answer = a * b
        question =
          options.orientation === 'horizontal' ? `${a} × ${b}` : `${a}\n×\n${b}`
        break

      case 'division':
        b = generateRandomInt(2, 12)
        const quotient = generateRandomInt(2, 12)
        a = b * quotient
        a = applyNumberType(a, options.numberType)
        b = applyNumberType(b, options.numberType)
        answer = a / b
        question =
          options.orientation === 'horizontal' ? `${a} ÷ ${b}` : `${a}\n÷\n${b}`
        break

      case 'exponent':
        let exponent = 2

        if (options.exponentType === 'squares') {
          exponent = 2
        } else if (options.exponentType === 'cubes') {
          exponent = 3
        } else if (
          options.exponentType === 'custom' &&
          options.customExponent
        ) {
          exponent = options.customExponent
        } else if (options.exponentType === 'mixed') {
          exponent = generateRandomInt(2, 4)
        }

        const maxBase = Math.floor(
          Math.pow(options.maxExponentResult, 1 / exponent),
        )
        a = generateRandomInt(2, Math.min(maxBase, 15))
        a = applyNumberType(a, options.numberType)
        answer =
          Math.pow(Math.abs(a), exponent) *
          (a < 0 && exponent % 2 === 1 ? -1 : 1)
        question = `${a}^${exponent}`
        break

      case 'radical':
        if (options.radicalType === 'perfect_squares') {
          answer = generateRandomInt(2, 15)
          answer = applyNumberType(answer, options.numberType)
          a = answer * answer
          question = `√${Math.abs(a)}`
          answer = Math.abs(answer)
        } else {
          const perfect = generateRandomInt(2, 10)
          const remaining = generateRandomInt(2, 5)
          a = perfect * perfect * remaining
          question = `√${a}`
          answer = `${perfect}√${remaining}`
        }
        break
    }

    // Handle decimals if needed (only for non-fraction problems)
    if (options.numberFormat === 'decimal' && typeof answer === 'number') {
      answer = (answer + Math.random() * 0.9).toFixed(1)
    }
  }

  // Generate distractors for multiple choice based on answerChoiceCount
  const choiceCount = options.answerChoiceCount || 4
  const options_array = new Set<number | string>()
  options_array.add(answer)

  while (options_array.size < choiceCount) {
    let distractor: number | string

    if (typeof answer === 'string' && answer.includes('/')) {
      // Generate fraction distractors
      const parts = answer.split('/')
      const ansNum = parseInt(parts[0])
      const ansDen = parts.length > 1 ? parseInt(parts[1]) : 1

      // Create variations
      const variation = Math.random()
      if (variation < 0.33) {
        // Change numerator
        distractor = formatFraction(ansNum + generateRandomInt(-2, 2), ansDen)
      } else if (variation < 0.66) {
        // Change denominator
        distractor = formatFraction(ansNum, ansDen + generateRandomInt(-2, 2))
      } else {
        // Different fraction
        const newFrac = generateFraction()
        distractor = formatFraction(newFrac.numerator, newFrac.denominator)
      }

      if (
        distractor === answer ||
        distractor.includes('0') ||
        distractor.includes('-')
      )
        continue
    } else if (typeof answer === 'number') {
      const offset = generateRandomInt(-10, 10)
      if (offset === 0) continue
      distractor = answer + offset

      if (options.numberFormat === 'decimal') {
        distractor = distractor.toFixed(1)
      }
    } else {
      // For string answers (like simplified radicals)
      distractor = generateRandomInt(1, 20)
    }

    options_array.add(distractor)
  }

  return {
    id: Math.random().toString(36).substr(2, 9),
    question: question,
    answer,
    type: selectedType,
    options: Array.from(options_array).sort(() => Math.random() - 0.5),
  }
}
